import type Store from '@ember-data/store';
import type AlertingService from '@mylighthouse/prism-ember/services/alerting';
import type { Ota } from 'frontend-utils/models';

/**
 * Hard refresh the app when an OTA is disconnected while the store state still
 * has it listed as connected. Branddotcom is ignored in this check.
 *
 * @param  { array } disconnectedOtas : A list of OTAs which are now disconnected.
 * @param  { object } store : The store service.
 * @param  { object } alerting : The alerting service.
 */

export function refreshIfOtaIsDisconnected(
  disconnectedOtas: string[],
  store: Store,
  alerting: AlertingService,
): void {
  (disconnectedOtas ?? [])
    .filter((disconnectedOtaId) => disconnectedOtaId !== 'branddotcom')
    .forEach((disconnectedOtaId) => {
      const disconnectedOta = store.peekRecord<Ota>('ota', disconnectedOtaId);
      // If the disconnected OTA is still connected according to the old store state, refresh the app
      if (disconnectedOta?.isConnected) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        alerting.showErrorAlert().then(() => {
          window.location.href = '/';
        });
      }
    });
}

/**
 * Sets the state of all the passed OTAs to disconnected.
 *
 * @param  { array } disconnectedOtas : A list of OTAs which are now disconnected.
 * @param  { object } store : The store service.
 */
export function updateDisconnectedOtas(
  disconnectedOtas: string[],
  store: Store,
): void {
  disconnectedOtas.forEach((disconnectedOtaId) => {
    const disconnectedOta = store.peekRecord<Ota>('ota', disconnectedOtaId);
    if (disconnectedOta) {
      disconnectedOta.isConnected = false;
    }
  });
}

/**
 * Sets the state of the branddotcom OTAs to connected.
 * This is used to reset the brand connected states between switching of different subscriptions in the app.
 *
 * @param  { object } store : The store service.
 */

export function reconnectBranddotcomOtas(store: Store): void {
  // The store gets destroyed when exiting the app. In tests this causes an issue if the function would still be called after.
  if (store.isDestroying || store.isDestroyed) {
    return;
  }

  const brandOtaIds = ['branddotcom', 'branddotcomsec'];
  brandOtaIds.forEach((otaId) => {
    const ota = store.peekRecord<Ota>('ota', otaId);
    if (ota) {
      ota.isConnected = true;
    }
  });
}
